/* ============================================================================
   SF TRY-ON COMPOSITOR
   ----------------------------------------------------------------------------
   Percentage-based watch-head + strap compositor for the SF Try-On V2 section.

   Ported from the proven OrixGB Strap Visualizer engine (_computeSplit) which
   itself refines watch-strap-customizer.js. We DO NOT touch those source files;
   this is a self-contained, Try-On-scoped port so the strap finder owns its own
   visualisation without pulling in carousel/cart/tab logic it doesn't need.

   Responsive by construction: every layer is sized/positioned in % inside a
   fixed-aspect-ratio stage, and a ResizeObserver re-runs the math on resize /
   orientation change (mobile, iPad, desktop).

   Calibration: exposes the same instance interface the existing
   watch-strap-calibrator.js expects — it attaches to [data-watch-strap-customizer]
   and reads el._wscInstance.{ getCurrentWatch, getCurrentStrap, calibData,
   recomputeStage }. Its split-mode export keys already match this theme's
   metaobject fields (watchhead position_data + strap top_data/bottom_data).
   ========================================================================== */

(function () {
  'use strict';

  var STAGE_ASPECT = 0.75; // width / height → 3:4 stage (matches existing CSS)

  // Locked fallback geometry (mirrors the Visualizer's OSV_LOCKED). Used when a
  // watch head metaobject has no position_data, so every watch still renders well.
  var LOCKED = {
    widthPct:        0.53,
    topOffsetPct:    0.25,
    lugAttachTop:    { x: 0.5, y: 0.175 },
    lugAttachBottom: { x: 0.5, y: 0.82 },
    caseWidthAtLug:  0.72
  };

  function pct(v, b) { return (v / b * 100).toFixed(4) + '%'; }

  // Map a StrapFinderState strap object to the field names the engine math uses.
  function mapStrap(s) {
    if (!s) return null;
    return {
      id:             s.id,
      name:           s.name,
      topImageUrl:    s.imageTopUrl    || s.topImageUrl    || null,
      bottomImageUrl: s.imageBottomUrl || s.bottomImageUrl || null,
      topImageW:      s.topImageW    || null,
      topImageH:      s.topImageH    || null,
      bottomImageW:   s.bottomImageW || null,
      bottomImageH:   s.bottomImageH || null,
      topData:        s.topData    || null,
      bottomData:     s.bottomData || null,
      attachmentData: s.attachmentData || null
    };
  }

  function TryonCompositor(stage, watch) {
    this.stage  = stage;
    this.watch  = watch || {};          // { imageUrl, imageWidth, imageHeight, positionData, name }
    this.strap  = null;                 // mapped current strap
    this.calibData = null;              // set live by watch-strap-calibrator.js
    this.config = { stageAspectRatio: STAGE_ASPECT };

    this.topLayer   = stage.querySelector('[data-sf-strap-top]');
    this.botLayer   = stage.querySelector('[data-sf-strap-bottom]');
    this.watchLayer = stage.querySelector('[data-sf-watch-layer]');
    this.topImg = this.topLayer ? this.topLayer.querySelector('img') : null;
    this.botImg = this.botLayer ? this.botLayer.querySelector('img') : null;
    this.watchImg = this.watchLayer ? this.watchLayer.querySelector('img') : null;

    var self = this;

    // Backfill watch dimensions + recompute once the head image decodes.
    if (this.watchImg) this.watchImg.addEventListener('load', function () {
      if (this.naturalWidth) {
        self.watch.imageWidth  = this.naturalWidth;
        self.watch.imageHeight = this.naturalHeight;
      }
      self.recomputeStage();
    });

    // Recompute when strap halves finish loading; backfill natural dimensions
    // when Liquid didn't provide them (e.g. metaobject-only straps) so the
    // attach-point math uses the real image aspect ratio.
    if (this.topImg) this.topImg.addEventListener('load', function () {
      if (self.strap && !self.strap.topImageW && this.naturalWidth) {
        self.strap.topImageW = this.naturalWidth;
        self.strap.topImageH = this.naturalHeight;
      }
      self.recomputeStage();
    });
    if (this.botImg) this.botImg.addEventListener('load', function () {
      if (self.strap && !self.strap.bottomImageW && this.naturalWidth) {
        self.strap.bottomImageW = this.naturalWidth;
        self.strap.bottomImageH = this.naturalHeight;
      }
      self.recomputeStage();
    });

    // Responsive: re-run math on any stage / panel resize or window resize.
    // RAF wrapper ensures CSS layout has settled before we read offsetWidth/Height.
    var _pending = false;
    function _scheduleRecompute() {
      if (_pending) return;
      _pending = true;
      requestAnimationFrame(function () { _pending = false; self.recomputeStage(); });
    }
    if (window.ResizeObserver) {
      this._ro = new ResizeObserver(_scheduleRecompute);
      this._ro.observe(stage);
      // Also observe the parent panel so breakpoint transitions are caught even
      // when the stage itself doesn't change size (e.g. height-constrained layouts).
      if (stage.parentElement) this._ro.observe(stage.parentElement);
    }
    // Window resize as a universal safety net (handles orientation change on mobile).
    this._resizeFn = _scheduleRecompute;
    window.addEventListener('resize', this._resizeFn);
  }

  /* ── Calibrator interface ─────────────────────────────────────────────── */
  TryonCompositor.prototype.getCurrentWatch = function () {
    var pd = this.watch.positionData || {};
    return {
      id:              this.watch.id || 'tryon-watch',
      name:            this.watch.name || 'Watch',
      widthPct:        (pd.widthPct        != null) ? pd.widthPct        : LOCKED.widthPct,
      topOffsetPct:    (pd.topOffsetPct    != null) ? pd.topOffsetPct    : LOCKED.topOffsetPct,
      lugAttachTop:    pd.lugAttachTop    || LOCKED.lugAttachTop,
      lugAttachBottom: pd.lugAttachBottom || LOCKED.lugAttachBottom,
      caseWidthAtLug:  (pd.caseWidthAtLug  != null) ? pd.caseWidthAtLug  : LOCKED.caseWidthAtLug,
      imageWidth:      this.watch.imageWidth  || 1200,
      imageHeight:     this.watch.imageHeight || 1200
    };
  };

  TryonCompositor.prototype.getCurrentStrap = function () { return this.strap; };

  /* ── Split-image composition math (ported from OrixGB Visualizer) ─────── */
  // stageH is passed from recomputeStage using the DOM's actual offsetHeight so
  // that CSS constraints (max-width, flex sizing) are respected without breaking
  // the percentage math — the fallback keeps it backward-compatible.
  TryonCompositor.prototype._computeSplit = function (watch, strap, stageWidth, stageH) {
    if (stageH == null) stageH = stageWidth / (this.config.stageAspectRatio || 0.75);
    var cd = this.calibData; // set by the calibrator; null for normal visitors

    var wp = watch; // already flattened via getCurrentWatch()
    var widthPct       = cd ? cd.watch.widthPct       : wp.widthPct;
    var topOffsetPct   = cd ? cd.watch.topOffsetPct   : wp.topOffsetPct;
    var lugTopY        = cd ? cd.watch.lugTopY        : wp.lugAttachTop.y;
    var lugBottomY     = cd ? cd.watch.lugBottomY     : wp.lugAttachBottom.y;
    var caseWidthAtLug = cd ? cd.watch.caseWidthAtLug : wp.caseWidthAtLug;

    var wW = stageWidth * widthPct;
    var wH = wW / ((watch.imageWidth || 1200) / (watch.imageHeight || 1200));
    var wX = (stageWidth - wW) / 2;
    var wY = stageH * topOffsetPct;

    // Decoupled horizontal centers (x can differ per lug if calibrated)
    var topX_frac = (cd && cd.watch.lugTopX    !== undefined) ? cd.watch.lugTopX
                    : (wp.lugAttachTop.x    !== undefined ? wp.lugAttachTop.x    : 0.5);
    var botX_frac = (cd && cd.watch.lugBottomX !== undefined) ? cd.watch.lugBottomX
                    : (wp.lugAttachBottom.x !== undefined ? wp.lugAttachBottom.x : 0.5);
    var centerXTop    = wX + topX_frac * wW;
    var centerXBottom = wX + botX_frac * wW;
    var lugTopYpx     = wY + lugTopY    * wH;
    var lugBottomYpx  = wY + lugBottomY * wH;

    // Defaults for straps without calibrated attach data
    var defaultTopAttachY = 1.0;
    var defaultBotAttachY = 0.0;
    var defaultLugWidth   = 1.5;

    // Auto-scale top lug width by the two halves' width ratio when uncalibrated
    var computedTLW = defaultLugWidth;
    if (!strap.topData && !strap.bottomData && strap.topImageW && strap.bottomImageW) {
      computedTLW = defaultLugWidth * (strap.bottomImageW / strap.topImageW);
    }

    var td = strap.topData    || { attachPt: { x: 0.5, y: defaultTopAttachY }, lugWidth: computedTLW };
    var bd = strap.bottomData || { attachPt: { x: 0.5, y: defaultBotAttachY }, lugWidth: defaultLugWidth };

    var tAX = cd ? cd.top.attachX  : ((td.attachPt || {}).x !== undefined ? td.attachPt.x : 0.5);
    var tAY = cd ? cd.top.attachY  : ((td.attachPt || {}).y !== undefined ? td.attachPt.y : defaultTopAttachY);
    var tLW = cd ? cd.top.lugWidth : (td.lugWidth || computedTLW);
    var tW  = wW * (caseWidthAtLug / tLW);
    var tH  = tW / ((strap.topImageW || 400) / (strap.topImageH || 600));
    var tX  = centerXTop - tAX * tW;
    var tY  = lugTopYpx  - tAY * tH;

    var bAX = cd ? cd.bottom.attachX  : ((bd.attachPt || {}).x !== undefined ? bd.attachPt.x : 0.5);
    var bAY = cd ? cd.bottom.attachY  : ((bd.attachPt || {}).y !== undefined ? bd.attachPt.y : defaultBotAttachY);
    var bLW = cd ? cd.bottom.lugWidth : (bd.lugWidth || defaultLugWidth);
    var bW  = wW * (caseWidthAtLug / bLW);
    var bH  = bW / ((strap.bottomImageW || 400) / (strap.bottomImageH || 800));
    var bX  = centerXBottom - bAX * bW;
    var bY  = lugBottomYpx  - bAY * bH;

    return {
      watch:  { width: pct(wW, stageWidth), height: pct(wH, stageH), left: pct(wX, stageWidth), top: pct(wY, stageH) },
      top:    { width: pct(tW, stageWidth), height: pct(tH, stageH), left: pct(tX, stageWidth), top: pct(tY, stageH) },
      bottom: { width: pct(bW, stageWidth), height: pct(bH, stageH), left: pct(bX, stageWidth), top: pct(bY, stageH) }
    };
  };

  function applyBox(el, box) {
    if (!el || !box) return;
    el.style.width  = box.width;
    el.style.height = box.height;
    el.style.left   = box.left;
    el.style.top    = box.top;
  }

  /* ── Re-run the math and apply to the DOM ─────────────────────────────── */
  TryonCompositor.prototype.recomputeStage = function () {
    var stageW = this.stage.offsetWidth;
    if (!stageW) return;
    // Use the DOM's actual height so any CSS constraint (max-width, flex, etc.)
    // that breaks the 3:4 assumption is automatically accounted for.
    var stageH = this.stage.offsetHeight || (stageW / (this.config.stageAspectRatio || 0.75));

    var watch = this.getCurrentWatch();
    var comp  = this._computeSplit(watch, this.strap || {
      topData: null, bottomData: null
    }, stageW, stageH);

    applyBox(this.watchLayer, comp.watch);
    if (this.strap) {
      applyBox(this.topLayer, comp.top);
      applyBox(this.botLayer, comp.bottom);
    }
  };

  /* ── Select a strap → toggle halves + recompute ───────────────────────── */
  TryonCompositor.prototype.setStrap = function (rawStrap) {
    var s = mapStrap(rawStrap);
    this.strap = s;

    var hasTop = !!(s && s.topImageUrl);
    var hasBot = !!(s && s.bottomImageUrl);

    if (this.topLayer) {
      this.topLayer.hidden = !hasTop;
      if (hasTop && this.topImg) this.topImg.src = s.topImageUrl;
      else if (this.topImg) this.topImg.removeAttribute('src');
    }
    if (this.botLayer) {
      this.botLayer.hidden = !hasBot;
      if (hasBot && this.botImg) this.botImg.src = s.bottomImageUrl;
      else if (this.botImg) this.botImg.removeAttribute('src');
    }

    this.recomputeStage();
  };

  /* ── Boot ─────────────────────────────────────────────────────────────── */
  var instances = [];
  var globalListenersBound = false;

  function selectFromState(compositor) {
    var SFS = window.StrapFinderState;
    if (SFS && SFS.selectedStrap) compositor.setStrap(SFS.selectedStrap);
    else compositor.recomputeStage();
  }

  function bindGlobalListeners() {
    if (globalListenersBound) return;
    globalListenersBound = true;
    document.addEventListener('sf:strap:select', function (e) {
      instances.forEach(function (compositor) {
        compositor.setStrap(e.detail && e.detail.strap);
      });
    });
    document.addEventListener('sf:state:init', function () {
      instances.forEach(selectFromState);
    });
  }

  function init() {
    document.querySelectorAll('[data-sf-tryon-stage]').forEach(function (stage) {
      if (stage._tryonCompositor) return;

      var watch = {};
      // Sections emit the seed blob as a sibling of the stage, not inside it —
      // look inside first, then within the enclosing section.
      var blob = stage.querySelector('[data-sf-watchhead-json]');
      if (!blob) {
        var scope = stage.closest('.shopify-section') || stage.parentElement;
        if (scope) blob = scope.querySelector('[data-sf-watchhead-json]');
      }
      if (blob) {
        try { watch = JSON.parse(blob.textContent) || {}; }
        catch (e) { watch = {}; }
      }

      var compositor = new TryonCompositor(stage, watch);
      stage._tryonCompositor = compositor;
      // Calibrator hook: it polls for [data-watch-strap-customizer]._wscInstance.
      stage._wscInstance = compositor;
      // Notify listeners that the compositor is ready (event-driven init)
      stage.dispatchEvent(new CustomEvent('tryon:compositor:ready'));
      instances.push(compositor);

      // If state already initialised before this script ran, apply immediately.
      var boot = window.StrapFinderState;
      if (boot && boot._ready) selectFromState(compositor);
      else compositor.recomputeStage();
    });
    bindGlobalListeners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
